"use client";

import { cn } from "@/lib/utils";
import { ServerProductCard } from "@/components/features/listings/ServerProductCard";
import type { ServerProductCardItem } from "@/components/features/listings/product-card-display";
import { SaveListingButton } from "@/components/features/listings/save-listing-button";
import { BROWSE_CARD_SIZES } from "@/components/features/listings/image-sizes";
import { listingHref } from "@shared/routing/listing-href";
import type { BrowseListing } from "@/components/listing/browse/marketplace-listing";
import { catalogueGridClassName } from "./catalogue-shell";

interface CardGridProps {
  requestedQuantity?: number;
  products: Array<BrowseListing>;
  isLoading?: boolean;
  mapVisible?: boolean;
  hoveredRowId: string | null;
  onHoverRow: (id: string | null) => void;
  onSelect: (product: BrowseListing) => void;
}

function toCardItem(product: BrowseListing): ServerProductCardItem {
  return {
    _id: product._id,
    title: product.title,
    buyerUnitPriceCents: product.buyerUnitPriceCents,
    primaryImageUrl: product.primaryImageUrl,
    slug: product.slug,
    publicIdentifier: product.publicIdentifier,
    cityName: product.cityName,
    stateAbbr: product.stateAbbr,
    originZip: product.originZip,
    checkoutEnabled: product.checkoutEnabled,
    intent: product.intent ?? null,
    condition: product.condition,
    productTypeSlug: product.productTypeSlug,
    quantityAvailable: product.availableToSell,
    dimensions: product.dimensions,
    shippingMethods: product.shippingMethods,
    productTypeName: product.productType?.displayName,
  };
}

export function quoteHrefFor(
  product: BrowseListing,
  requestedQuantity?: number,
): string {
  const [path] = listingHref(product).split("?");
  const search = new URLSearchParams();
  if (
    requestedQuantity !== undefined &&
    Number.isSafeInteger(requestedQuantity) &&
    requestedQuantity > 1
  ) {
    search.set("qty", String(requestedQuantity));
  }
  const query = search.toString();
  return `${path}/request-quote${query ? `?${query}` : ""}`;
}

export function CardGridSkeleton({ mapVisible }: { mapVisible: boolean }) {
  return (
    <div className={catalogueGridClassName(mapVisible)}>
      {Array.from({ length: 6 }).map((_, index) => (
        <div
          key={`listing-skeleton-${index}`}
          className="animate-pulse overflow-hidden rounded-lg shadow-card max-md:flex max-md:items-center max-md:gap-3.5 max-md:rounded-none max-md:shadow-none max-md:px-4 max-md:py-2"
        >
          <div className="aspect-[3/2] bg-muted max-md:size-24 max-md:shrink-0 max-md:rounded-lg" />
          <div className="space-y-2 p-3.5 max-md:flex-1 max-md:p-0">
            <div className="h-4 w-1/3 rounded bg-muted" />
            <div className="h-4 w-3/4 rounded bg-muted" />
            <div className="h-3 w-1/2 rounded bg-muted" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function CardGrid({
  products,
  isLoading,
  mapVisible = false,
  hoveredRowId,
  onHoverRow,
  onSelect,
  requestedQuantity,
}: CardGridProps) {
  if (isLoading && products.length === 0) {
    return <CardGridSkeleton mapVisible={mapVisible} />;
  }

  return (
    <div className={catalogueGridClassName(mapVisible)}>
      {products.map((product, index) => (
        <div
          key={product._id}
          onMouseEnter={() => onHoverRow(product._id)}
          onMouseLeave={() => onHoverRow(null)}
          onClick={() => onSelect(product)}
          className={cn(
            "rounded-lg transition-shadow max-md:rounded-none",
            hoveredRowId === product._id &&
              "ring-2 ring-action ring-offset-2 ring-offset-background",
          )}
        >
          <ServerProductCard
            item={toCardItem(product)}
            requestedQuantity={requestedQuantity}
            priority={index === 0}
            sizes={mapVisible ? BROWSE_CARD_SIZES.map : BROWSE_CARD_SIZES.full}
            quoteHref={quoteHrefFor(product, requestedQuantity)}
            phoneRow
            saveSlot={
              <SaveListingButton
                productId={product._id}
                listingTitle={product.title}
                listingImageUrl={product.primaryImageUrl ?? undefined}
                presentation="overlay"
              />
            }
          />
        </div>
      ))}
    </div>
  );
}
